class ConstructorAutocomplete extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allInstances = document.querySelectorAll('constructor-autocomplete');
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.pageBody = document.getElementsByTagName('body')[0];
    this.statusElement = this.querySelector('.predictive-search-status');
    this.loadingText = this.getAttribute('data-loading-text');
    this._mobileTabsInitialised = false;
    this._debounceTimer = null;

    this._setupAutocompleteListeners();
  }

  _debounce(fn, delay) {
    return (...args) => {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  _setupAutocompleteListeners() {
    this.input.form.addEventListener('submit', this._onFormSubmit.bind(this));
    this.input.addEventListener('focus', this._onFocus.bind(this));
    this.addEventListener('focusout', this._onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      clearTimeout(this._debounceTimer);
      this.close(true);
      return;
    }

    const debounceMs = window.constructorStrings?.autocompleteDebounceMs ?? 300;
    this._debounce(this._getAutocompleteResults, debounceMs)(this.searchTerm);
  }

  _onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    } else {
      this.saveLastSearched();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  _onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.hasAttribute('results')) {
      this.open();
    } else {
      this._getAutocompleteResults(this.searchTerm);
    }
  }

  _onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this._switchOption('up');
        break;
      case 'ArrowDown':
        this._switchOption('down');
        break;
      case 'Enter':
        this._selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  _switchOption(direction) {
    if (!this.hasAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null,
    );

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;
    while (selectedElementIndex === -1 && i < allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) selectedElementIndex = i;
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    let activeElementIndex = 0;
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', 'true');
    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  _selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  _setLoadingState() {
    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = this.loadingText;
      setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
    }
    this.setAttribute('loading', '');
  }

  async _getAutocompleteResults(searchTerm) {
    if (!window.constructorio?.autocomplete?.getAutocompleteResults) return;

    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this._setLoadingState();

    if (this.cachedResults[queryKey]) {
      this._renderResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      const productsLimit = window.constructorStrings?.autocompleteProductsLimit || 9;
      const suggestionsLimit = window.constructorStrings?.autocompleteSuggestionsLimit || 5;
      const categoriesLimit = window.constructorStrings?.autocompleteCategoriesLimit || 6;

      const result = await window.constructorio.autocomplete.getAutocompleteResults(searchTerm, {
        resultsPerSection: { Products: productsLimit, 'Search Suggestions': suggestionsLimit, Categories: categoriesLimit },
      });

      console.log('[ConstructorAutocomplete] autocomplete results:', result);

      if (this.abortController.signal.aborted) return;

      const productHandles = (result?.sections?.['Products'] ?? [])
        .map((item) => item.data?.id || item.data?.handle)
        .filter(Boolean)
        .map((h) => encodeURIComponent(h));

      const productItemsHtml = productHandles.length > 0 ? await this._fetchProductsHtml(productHandles) : '';

      if (this.abortController.signal.aborted) return;

      const resultWithHtml = { ...result, _productItemsHtml: productItemsHtml };

      this.allInstances.forEach((instance) => {
        instance.cachedResults[queryKey] = resultWithHtml;
      });

      this._renderResults(resultWithHtml);

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.searchQuery, {
          query: searchTerm,
          search_context: 'overlay',
          engine: 'constructor',
          products_count: result?.sections?.['Products']?.length || 0,
          collections_count: result?.sections?.['Search Suggestions']?.length || 0,
          result_product_ids: productHandles.map(decodeURIComponent),
        });
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[ConstructorAutocomplete] Failed to fetch autocomplete results:', error);
      window.reportFrontendError?.({
        source: 'constructor',
        surface: 'ConstructorAutocomplete',
        message: error?.message || String(error),
        stack: error?.stack || '',
        error_type: 'api',
        context: { searchTerm: this.searchTerm },
      });
      this.close();
    }
  }

  async _fetchProductsHtml(handles) {
    const baseUrl = window.routes?.all_products_collection_url || '/collections/all';
    const { signal } = this.abortController;
    const BATCH_SIZE = 20;
    const batches = [];
    for (let i = 0; i < handles.length; i += BATCH_SIZE) {
      batches.push(handles.slice(i, i + BATCH_SIZE));
    }

    const campaign_code = new URLSearchParams(window.location.search).get('code');

    const htmlParts = await Promise.all(
      batches.map((batch) => {
        const url =
          `${baseUrl}?view=list-products&limit=${batch.length}&type=${batch.join('+')}` +
          (campaign_code ? `&q=${campaign_code}` : '');
        return fetch(url, { signal }).then((r) => {
          if (!r.ok) throw new Error(`list-products responded with ${r.status}`);
          return r.text();
        });
      }),
    );

    return htmlParts.join('');
  }

  _escapeHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  _renderResults(result) {
    const sections = result?.sections ?? {};
    const suggestions = sections['Search Suggestions'] ?? [];
    const categories = sections['Categories'] ?? [];
    const productItemsHtml = result._productItemsHtml || '';

    const renderedProductCount = productItemsHtml ? (productItemsHtml.match(/js-fetched-item/g) || []).length : 0;
    const totalResults = suggestions.length + categories.length + renderedProductCount;
    const hasProducts = renderedProductCount > 0;

    this.predictiveSearchResults.innerHTML = '';

    if (totalResults === 0) {
      const noResultsFrag = this._buildNoResultsHTML(this.searchTerm);
      if (noResultsFrag) this.predictiveSearchResults.appendChild(noResultsFrag);
      this.setAttribute('results', '');
      this.open();
      this.removeAttribute('loading');
      this.displayLastSearched();
      this.clearLastSearched();
      return;
    }

    const wrapperTpl = document.getElementById('constructor-predictive-search-results');
    if (!wrapperTpl) {
      console.warn('[ConstructorAutocomplete] Missing #constructor-predictive-search-results template.');
      return;
    }

    const fragment = wrapperTpl.content.cloneNode(true);
    const groupsEl = fragment.querySelector('[data-predictive-search-groups]');
    const buttonSlot = fragment.querySelector('[data-predictive-search-button-slot]');
    const countEl = fragment.querySelector('[data-predictive-search-live-region-count-value]');

    if (groupsEl && !hasProducts) {
      groupsEl.classList.add('predictive-search__results-groups-wrapper--no-products');
    }

    if (groupsEl) {
      const suggestionsFrag = this._buildSuggestionsHTML(suggestions, categories);
      if (suggestionsFrag) groupsEl.appendChild(suggestionsFrag);

      if (hasProducts) {
        const productsFrag = this._buildProductsHTML(productItemsHtml);
        if (productsFrag) groupsEl.appendChild(productsFrag);
      }
    }

    if (buttonSlot) {
      const btnFrag = this._buildSearchForButtonHTML();
      if (btnFrag) buttonSlot.replaceWith(btnFrag);
      else buttonSlot.remove();
    }

    if (countEl) countEl.textContent = String(totalResults);

    this.predictiveSearchResults.appendChild(fragment);
    this.setAttribute('results', '');
    this.open();
    this.removeAttribute('loading');

    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = String(totalResults);
      setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
    }

    this.displayLastSearched();
    this.clearLastSearched();
    this._highlightSearchedWord();
    this._mobileTabs();
  }

  _buildSuggestionsHTML(suggestions, categories = []) {
    const wrapperTpl = document.getElementById('constructor-predictive-search-suggestions');
    if (!wrapperTpl) return null;

    const wrapper = wrapperTpl.content.cloneNode(true);
    const suggestionsSlot = wrapper.querySelector('[data-predictive-search-suggestions-slot]');
    const categoriesSlot = wrapper.querySelector('[data-predictive-search-categories-slot]');
    const lastSearchedSlot = wrapper.querySelector('[data-predictive-search-last-searched-slot]');
    const itemTpl = document.getElementById('constructor-predictive-search-suggestion-item');

    // Search Suggestions
    if (suggestions.length > 0 && suggestionsSlot && itemTpl) {
      const listTpl = document.getElementById('constructor-predictive-search-suggestions-list');
      const listClone = listTpl?.content.cloneNode(true);
      const listEl = listClone?.querySelector('[data-predictive-search-suggestion-list]');
      if (listEl) {
        const searchUrl = window.routes?.search_url || '/search';
        suggestions.forEach((item, index) => {
          const itemFrag = itemTpl.content.cloneNode(true);
          const li = itemFrag.querySelector('[data-predictive-search-suggestion]');
          if (li) li.id = `predictive-search-option-suggestion-${index + 1}`;
          const link = itemFrag.querySelector('[data-predictive-search-suggestion-link]');
          if (link) {
            link.setAttribute('href', `${searchUrl}?q=${encodeURIComponent(item.value)}&options%5Bprefix%5D=last`);
          }
          const label = itemFrag.querySelector('[data-predictive-search-suggestion-label]');
          // textContent escapes naturally; _highlightSearchedWord later re-reads textContent
          // and writes innerHTML with <strong> around the matched portion.
          if (label) label.textContent = item.value;
          listEl.appendChild(itemFrag);
        });
        suggestionsSlot.replaceWith(listClone);
      } else {
        suggestionsSlot.remove();
      }
    } else if (suggestionsSlot) {
      suggestionsSlot.remove();
    }

    // Categories (collections) — rendered inside the same group so the flex layout keeps two columns.
    if (categories.length > 0 && categoriesSlot && itemTpl) {
      const listTpl = document.getElementById('constructor-predictive-search-categories-list');
      const listClone = listTpl?.content.cloneNode(true);
      const listEl = listClone?.querySelector('[data-predictive-search-category-list]');
      if (listEl) {
        categories.forEach((item, index) => {
          const handle = item.data?.handle;
          if (!handle) return;
          const itemFrag = itemTpl.content.cloneNode(true);
          const li = itemFrag.querySelector('[data-predictive-search-suggestion]');
          if (li) {
            li.id = `predictive-search-option-collection-${index + 1}`;
            li.setAttribute('data-cnstrc-item-section', 'Categories');
            li.setAttribute('data-cnstrc-item-name', item.value || '');
          }
          const link = itemFrag.querySelector('[data-predictive-search-suggestion-link]');
          if (link) link.setAttribute('href', `/collections/${encodeURIComponent(handle)}`);
          const label = itemFrag.querySelector('[data-predictive-search-suggestion-label]');
          if (label) label.textContent = item.value;
          listEl.appendChild(itemFrag);
        });
        categoriesSlot.replaceWith(listClone);
      } else {
        categoriesSlot.remove();
      }
    } else if (categoriesSlot) {
      categoriesSlot.remove();
    }

    if (lastSearchedSlot) {
      const lastFrag = this._buildLastSearchedHTML();
      if (lastFrag) lastSearchedSlot.replaceWith(lastFrag);
      else lastSearchedSlot.remove();
    }

    return wrapper;
  }

  _buildLastSearchedHTML() {
    const tpl = document.getElementById('constructor-predictive-search-last-searched');
    return tpl ? tpl.content.cloneNode(true) : null;
  }

  _buildProductsHTML(productItemsHtml) {
    const tpl = document.getElementById('constructor-predictive-search-products');
    if (!tpl) return null;

    const fragment = tpl.content.cloneNode(true);
    const listEl = fragment.querySelector('[data-predictive-search-products-list]');
    if (!listEl) return fragment;

    // Parse Shopify's fetched product cards and append the live <li> nodes directly —
    // they're already DOM, no need to round-trip through outerHTML/innerHTML.
    const parsed = new DOMParser().parseFromString(productItemsHtml, 'text/html');
    [...parsed.querySelectorAll('.js-fetched-item')].forEach((li, index) => {
      li.id = `predictive-search-option-product-${index + 1}`;
      li.classList.add(
        'predictive-search__list-item',
        'grid__item',
        'product-grid',
        'grid--3-col',
        'grid--4-col-desktop',
      );
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      listEl.appendChild(li);
    });

    return fragment;
  }

  _buildSearchForButtonHTML() {
    const tpl = document.getElementById('constructor-predictive-search-search-for');
    return tpl ? tpl.content.cloneNode(true) : null;
  }

  _buildNoResultsHTML(searchTerm = '') {
    const tpl = document.getElementById('constructor-predictive-search-no-results');
    if (!tpl) return null;

    const fragment = tpl.content.cloneNode(true);

    const descEl = fragment.querySelector('[data-predictive-search-no-results-description]');
    if (descEl) {
      const descriptionTemplate = window.constructorStrings?.noResultsForTerm || '';
      if (descriptionTemplate && searchTerm) {
        descEl.innerHTML = descriptionTemplate.replace('__terms__', `<strong>${this._escapeHtml(searchTerm)}</strong>`);
        descEl.hidden = false;
      }
    }

    const buttonSlot = fragment.querySelector('[data-predictive-search-button-slot]');
    if (buttonSlot) {
      const btnFrag = this._buildSearchForButtonHTML();
      if (btnFrag) buttonSlot.replaceWith(btnFrag);
      else buttonSlot.remove();
    }

    return fragment;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = 'calc(100svh - var(--header-search-panel-height))';
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.pageBody.classList.add('is_fixed');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    if (this.pageBody.classList.contains('is_fixed')) {
      this.pageBody.classList.remove('is_fixed');
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this._mobileTabsInitialised = false;
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.removeAttribute('style');
  }

  _highlightSearchedWord() {
    const currval = this.input.value;
    const onlyLower = currval === currval.toLowerCase();
    const escaped = currval.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const filterItems = this.querySelectorAll('[data-highlight]');
    for (let i = 0; i < filterItems.length; i++) {
      const textcontent = filterItems[i].textContent;
      const finalval = textcontent.replace(new RegExp(`(${escaped})`, 'i'), (_, p1) => {
        return '<strong>' + (onlyLower ? p1 : currval) + '</strong>';
      });
      filterItems[i].innerHTML = finalval;
    }
  }

  saveLastSearched() {
    const value = this.input.value.trim();
    if (!value) return;

    let stored = [];
    try {
      stored = JSON.parse(localStorage.getItem('lastSearched') || '[]');
    } catch {}

    stored = stored.filter((v) => v !== value);
    stored.push(value);
    if (stored.length > 5) stored = stored.slice(stored.length - 5);
    localStorage.setItem('lastSearched', JSON.stringify(stored));
  }

  displayLastSearched() {
    const container = this.querySelector('[data-latest-searches]');
    if (!container) return;

    let stored = [];
    try {
      stored = JSON.parse(localStorage.getItem('lastSearched') || '[]');
    } catch {}

    if (!stored.length) {
      container.closest('.small-hide')?.classList.add('hidden');
      return;
    }

    container.closest('.small-hide')?.classList.remove('hidden');
    const ul = document.createElement('ul');
    [...stored].reverse().forEach((value) => {
      const li = document.createElement('li');
      li.textContent = value;
      li.addEventListener('click', () => {
        this.input.value = value;
        this.onChange();
      });
      ul.appendChild(li);
    });
    container.innerHTML = '';
    container.appendChild(ul);
  }

  clearLastSearched() {
    const btn = this.querySelector('[data-clear-latest-searches]');
    if (!btn) return;

    btn.addEventListener('click', (event) => {
      event.preventDefault();
      localStorage.removeItem('lastSearched');
      const container = this.querySelector('[data-latest-searches]');
      if (container) container.innerHTML = '';
      btn.closest('.small-hide')?.classList.add('hidden');
    });
  }

  _mobileTabs() {
    if (this._mobileTabsInitialised) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    this._mobileTabsInitialised = true;
    const tabContents = this.querySelectorAll('.mobile-tab__content');
    const tabTriggers = this.querySelectorAll('.tab');
    tabTriggers.forEach((tabTrigger) => {
      tabTrigger.addEventListener('click', (event) => {
        const tabId = event.currentTarget.getAttribute('data-mobile-tab');
        Array.from(tabTriggers).forEach((tab) => tab.classList.remove('active'));
        Array.from(tabContents).forEach((tab) => (tab.style.display = 'none'));
        document.getElementById(tabId).style.display = 'flex';
        event.currentTarget.classList.add('active');
      });
    });
  }
}

customElements.define('constructor-autocomplete', ConstructorAutocomplete);
